import store from '@/store/store';
import { SANS_SUITE } from '@/configuration/optionBlocage/optionBlocage';
import { NORMAL, REGROUPEMENT, COMMENTAIRE } from '@/configuration/devisConf';
import { getAsync } from '@/api/devis';
import genererGuid from '@/services/general/genererGuid';
import { genererBudget, genererBudgetGeneration, transformProduitToPanneau } from '@/services/gestionPanneau/genererPanneau';
import gestionPanneauSousContrat from '@/services/gestionPanneau/gestionPanneauSousContrat';
import {
  isLigneLocation,
  isLigneTlpe,
  isLigneHonoraires,
  isLigneFraisTechnique,
  isLigneHonoraireFraisTraitement,
} from '@/services/devisCommandes/echeanciers/gestionEcheancier';
import { calculNetBudget } from '@/services/devisCommandes/calcul/calculRemisePanneau';
import { getPJsLogoByIds } from '@/services/piecesJointes/getPiecesJointeLogoByIds';
import { getIconPanneau } from '@/services/gestionIcon/iconColorGetter';
import { updateDataBudgetGenerationByBudgets } from '@/services/gestionPanneau/genererBudgetsPanneau';
import genererCopyDevisCommande from '@/services/devisCommandes/genererCopyDevisCommande';
import { calcPrixTotal } from '@/services/gestionPanneau/calcPanneau';
import calculArrondi from '@/services/calcul/calculArrondi';
import { quantiteDispoToPourcentageSurface } from '@/services/gestionPanneau/surfacePanneau';
import formatDate from '@/services/general/formatIsoDate';
import { ANNEE } from '@/configuration/devisConf';

function assignGuidsToPanneaux(lignesCommande) {
  const guidByProduitId = new Map();
  for (const ligne of lignesCommande) {
    if (ligne.type !== REGROUPEMENT) continue;
    if (ligne.produit && ligne.produit.guid) continue;
    const produitId = ligne.produit && ligne.produit.id;

    if (!produitId) continue;
    if (!guidByProduitId.has(produitId)) {
      guidByProduitId.set(produitId, genererGuid());
    }
    ligne.produit.guid = guidByProduitId.get(produitId);
  }
}
function regrouperLignesRegroupementParBloc(lignesRegroupement) {
  const blocsMap = new Map();
  for (const ligneRegroupement of lignesRegroupement) {
    const clePeriode = `${formatDate(ligneRegroupement.dateDebut)}|${formatDate(ligneRegroupement.dateFin)}`;
    if (!blocsMap.has(clePeriode)) blocsMap.set(clePeriode, []);
    blocsMap.get(clePeriode).push(ligneRegroupement);
  }
  return Array.from(blocsMap.values());
}

function getLigneRegroupementMedia(lignesRegroupementBloc) {
  const ligneAvecLocation = lignesRegroupementBloc.find(ligneRegroupement =>
    (ligneRegroupement.lignesCommande || []).some(ligne => ligne.type === NORMAL && isLigneLocation(ligne)));
  return ligneAvecLocation || lignesRegroupementBloc[0];
}

export function lignesCommandeToPanneaux(lignesCommande, societe, issueRenouvellement = false) {

  assignGuidsToPanneaux(lignesCommande);

  const panneauxMap = new Map();
  for (const ligne of lignesCommande) {
    if (ligne.type !== REGROUPEMENT) continue;
    const guid = ligne.produit && ligne.produit.guid;
    if (!guid) continue;
    if (!panneauxMap.has(guid)) {
      panneauxMap.set(guid, {
        panneau: transformProduitToPanneau(ligne.produit, ligne.fournisseur),
        lignesRegroupement: [],
      });
    }
    panneauxMap.get(guid).lignesRegroupement.push(ligne);
  }

  return Array.from(panneauxMap.values()).map(({ panneau, lignesRegroupement }) => {
    panneau.actif = lignesRegroupement[0] && lignesRegroupement[0].etatObtenu !== SANS_SUITE;
    panneau.pourcentageSurface = quantiteDispoToPourcentageSurface(lignesRegroupement[0] && lignesRegroupement[0].quantiteDispo);
    panneau.estRenouvellement = !!issueRenouvellement
      && lignesRegroupement.some(ligne => !!ligne.referenceRenouvellement);
    const blocs = regrouperLignesRegroupementParBloc(lignesRegroupement);
    panneau.budgets = blocs.map(lignesRegroupementBloc => {
      const ligneRegroupementMedia = getLigneRegroupementMedia(lignesRegroupementBloc);
      const ligneRegroupementHorsMedia = lignesRegroupementBloc.find(ligneRegroupement => ligneRegroupement !== ligneRegroupementMedia) || null;

      const budget = genererBudget();
      budget.guid = ligneRegroupementMedia.guid || budget.guid;
      budget.ligneCommandeId = ligneRegroupementMedia.id;
      if (ligneRegroupementHorsMedia) {
        budget.guidHorsMedia = ligneRegroupementHorsMedia.guid || null;
        budget.ligneCommandeIdHorsMedia = ligneRegroupementHorsMedia.id;
      }
      const lignesCommande = lignesRegroupementBloc
        .reduce((acc, ligneRegroupement) => acc.concat(ligneRegroupement.lignesCommande || []), []);

      for (const ligne of lignesCommande) {
        if (ligne.type !== NORMAL) continue;

        if (isLigneLocation(ligne)) {
          budget.dateDebut = ligne.dateDebut ? new Date(ligne.dateDebut) : null;
          budget.dateFin = ligne.dateFin ? new Date(ligne.dateFin) : null;
          budget.prixUnitaireBrut = ligne.prixUnitaire;
          budget.quantiteBrut = ligne.quantite != null ? ligne.quantite : 1;
          budget.uniteQteBrut = ligne.uniteVente || ANNEE;
          budget.prixBrutNegocie = calculArrondi(ligne.prixUnitaire * budget.quantiteBrut, 5);
          budget.ligneCommandeIds.location = ligne.id;
          budget.remise.remise = ligne.remise;
          panneau.categorie = {
            id: ligne.produit.id,
            libelle: ligne.produit.libelle,
            couleur: ligne.produit.couleurHtmlRgb,
          };
          panneau.icon = getIconPanneau(panneau);
          calculNetBudget(budget);
          continue;
        }
        if (isLigneTlpe(ligne, societe)) {
          budget.prixUnitaireTlpe = ligne.prixUnitaire;
          budget.quantiteTlpe = ligne.quantite != null ? ligne.quantite : 1;
          budget.uniteQteTlpe = ligne.uniteVente || ANNEE;
          budget.tlpe = calculArrondi(ligne.prixUnitaire * budget.quantiteTlpe, 5);
          budget.ligneCommandeIds.tlpe = ligne.id;
          continue;
        }
        if (isLigneHonoraires(ligne, societe, true)) {
          budget.honoraires = ligne.prixUnitaire;
          budget.ligneCommandeIds.honoraires = ligne.id;
          budget.modeCalculHonoraire = ligne.modeCalculHonoraire;
          continue;
        }
        if (isLigneFraisTechnique(ligne, societe)) {
          budget.fraisTechnique = ligne.prixUnitaire;
          budget.ligneCommandeIds.fraisTechnique = ligne.id;
          continue;
        }
        if (isLigneHonoraireFraisTraitement(ligne, societe)) {
          budget.honoraireFraisTraitement = ligne.prixUnitaire;
          budget.ligneCommandeIds.honoraireFraisTraitement = ligne.id;
          continue;
        }
      }

      const ligneCommentaire = lignesCommande.find(l => l.type === COMMENTAIRE);
      if (ligneCommentaire) panneau.commentaire = ligneCommentaire.libelle;

      budget.prixTotal = calcPrixTotal(budget);
      return budget;
    });

    panneau.budgetGeneration = genererBudgetGeneration();
    updateDataBudgetGenerationByBudgets(panneau.budgets, panneau.budgetGeneration);
    return panneau;
  });
}

async function getPanneauSousContrat(devis) {
  if (!devis || !devis.id) return [];
  const intervenantAnnonceur = devis.intervenants && devis.intervenants.find(i => i.annonceur);
  const pointDeVenteId = intervenantAnnonceur ? intervenantAnnonceur.tiersId : null;
  if (!pointDeVenteId) return [];
  const recommandationCompat = {
    id: devis.id,
    panneauxSousContrat: [],
    pointDeVente: { id: pointDeVenteId },
  };
  return gestionPanneauSousContrat(recommandationCompat);
}

function gestionTiersParentIntervenantDevis(devis) {
  const tiersIdMap = devis.intervenants.reduce((acc, interv) => {
    if (interv.tiers) acc[interv.tiersId] = interv.tiers;
    return acc;
  }, {});
  devis.intervenants.forEach(interv => {
    if (!interv.tiers || interv.tiers.tiersParent || !interv.tiers.tiersParentId) return;
    const parent = tiersIdMap[interv.tiers.tiersParentId];
    if (parent) interv.tiers.tiersParent = parent;
  });
}

export default async function devisToRecommandation(devisId, isCopy = false) {
  let devis = (await getAsync(devisId)).data.result;
  if (isCopy) devis = genererCopyDevisCommande(devis);

  const societes = store.getters['societe/societes'];
  const societe = societes.find(s => s.id === devis.societeId);
  await store.dispatch('societe/setSocieteId', societe.id);
  const tiersParentIds = devis.intervenants
    .filter(i => i.tiers && i.tiers.tiersParentId)
    .map(i => i.tiers.tiersParentId)
  const pieceJointes = await getPJsLogoByIds([
    ...devis.intervenants.map(i => i.tiersId),
    ...tiersParentIds,
    ...devis.lignesCommande.filter(l => l.produit).map(l => l.produit.id),
  ]);

  gestionTiersParentIntervenantDevis(devis);

  devis.intervenants.forEach(intervenant => {
    let pj = pieceJointes.find(p => p.elementId === intervenant.tiersId);
    if (!pj && intervenant.tiers && intervenant.tiers.tiersParentId) {
      pj = pieceJointes.find(p => p.elementId === intervenant.tiers.tiersParentId);
    }
    if (intervenant.tiers) {
      intervenant.tiers.srcImg = pj ? pj.chemin : null;
      intervenant.tiers.urlLogo = pj ? pj.chemin : null;
    }
  });

  devis.lignesCommande.forEach(ligne => {
    if (!ligne.produit) return;
    const pj = pieceJointes.find(p => p.elementId === ligne.produit.id);
    ligne.produit.srcImg = pj ? pj.chemin : null;
    ligne.produit.urlLogo = pj ? pj.chemin : null;
  });

  let panneauxSousContrat = [];
  if (!isCopy) panneauxSousContrat = await getPanneauSousContrat(devis);
  return { devis, societe, panneauxSousContrat };
}
